/**
 * "Is the document going away?" — the discriminator for cancelled requests (FR-154).
 *
 * The browser cancels every in-flight request when a page unloads, and a cancelled request reaches
 * the axios error interceptor indistinguishable from an unreachable server: both arrive with no
 * `error.response`. Treating the two alike means that navigating away while any API call is still in
 * flight replaces the page the user asked for with the network-error page — an error raised by their
 * own click, about a request whose answer nobody was waiting for any more. Observed on Firefox,
 * which cancels in-flight XHRs on navigation more eagerly than Chromium or WebKit.
 *
 * Axios cannot answer this. `axios.isCancel` and `code === 'ERR_CANCELED'` only cover cancellations
 * axios performs itself via an AbortController; a cancellation the BROWSER performs on unload
 * surfaces as a generic network error carrying no distinguishing code. Whether the document is
 * unloading is the only available signal, so it is tracked directly.
 *
 * Kept out of `client.ts` so it can be unit-tested: that module is globally mocked in tests to avoid
 * `import.meta.env` issues, exactly as `maintenanceReload` is.
 */

let unloading = false;

/**
 * Record that the document has begun unloading. Wired to `pagehide` and `beforeunload` by
 * `installUnloadListeners`; exported so tests can drive the state without dispatching events.
 */
export function markUnloading(): void {
  unloading = true;
}

/** Whether the document has begun unloading, i.e. pending requests are about to be cancelled. */
export function isUnloading(): boolean {
  return unloading;
}

/**
 * Reset to the not-unloading state. For tests only — a real document that starts unloading never
 * changes its mind, and one restored from the back/forward cache gets a fresh module instance.
 */
export function resetUnloadingForTests(): void {
  unloading = false;
}

/**
 * Whether a failed request proves the caller's token is no longer good.
 *
 * Only the server saying 401 proves that. A request that was cancelled, timed out, never left the
 * machine, or hit a 5xx says nothing whatsoever about the token — so treating those alike and
 * discarding the token logs a user out for reasons that have nothing to do with their session, and
 * loses the credential that would have made a retry work.
 *
 * Deliberately structural rather than an `instanceof AxiosError` check: callers pass whatever the
 * api layer threw, and a narrowed shape is enough to read the status.
 */
export function provesTokenInvalid(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === 401;
}

/**
 * Listen for the document going away.
 *
 * Both events, because neither alone is sufficient: `beforeunload` does not fire when a page enters
 * the back/forward cache, and `unload`/`beforeunload` are unreliable on mobile Safari, where
 * `pagehide` is the supported signal. Registering both costs nothing — `markUnloading` is idempotent.
 */
export function installUnloadListeners(target: Window): void {
  target.addEventListener('pagehide', markUnloading);
  target.addEventListener('beforeunload', markUnloading);
}
